import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper, Typography, IconButton, Select, MenuItem, Box, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Tooltip, Card, CardContent, Button, ButtonGroup, TableSortLabel
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  ArrowBack, KeyboardArrowDown, KeyboardArrowUp,
  UnfoldMore, UnfoldLess,
  Visibility, VisibilityOff,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AppContext from '../components/AppContext';
import { getResumJerarquic } from '../services/concepteGraphService';
import { listAnys, listMesos } from '../functions/common-lists';
import { formatEuro } from '../functions/formatNumber';
import { getColorForAmount } from '../functions/colors';
import { ConcepteIcon } from '../components/common/IconMap';
import useBack from '../hooks/useBack';

// Càlcul recursiu dels moviments pendents de reclassificar per a tot l'arbre sota un node
const getReclassificationStats = (node) => {
  if (!node || !node.fills || !Array.isArray(node.fills) || node.fills.length === 0) {
    return {
      directPending: 0,
      descendantPending: 0,
      totalPending: 0
    };
  }

  const sumChildrenMoviments = node.fills.reduce((acc, f) => acc + (f.numMoviments || 0), 0);
  const directPending = Math.max(0, (node.numMoviments || 0) - sumChildrenMoviments);

  let descendantPending = 0;
  node.fills.forEach(child => {
    const childStats = getReclassificationStats(child);
    descendantPending += childStats.totalPending;
  });

  return {
    directPending,
    descendantPending,
    totalPending: directPending + descendantPending
  };
};

// Càlcul del saldo net tenint en compte els signes
const getNodeSaldo = (n) => {
  if (n.saldo !== undefined && n.saldo !== null) return n.saldo;
  const ingres = n.totalIngres || 0;
  const despesa = Math.abs(n.totalDespesa || 0);
  return ingres - despesa;
};

// Funció d'ordenació flexible per a la jerarquia
const sortHierarchyNodes = (nodes, sortConfig) => {
  if (!nodes || !Array.isArray(nodes)) return [];
  const { key, direction } = sortConfig;
  const dir = direction === 'asc' ? 1 : -1;

  return [...nodes].sort((a, b) => {
    let valA, valB;
    switch (key) {
      case 'concepte':
        return dir * (a.nom || '').localeCompare(b.nom || '');
      case 'numMoviments':
        valA = a.numMoviments || 0;
        valB = b.numMoviments || 0;
        return dir * (valA - valB);
      case 'pes':
        valA = typeof a.pes === 'number' ? a.pes : 0;
        valB = typeof b.pes === 'number' ? b.pes : 0;
        return dir * (valA - valB);
      case 'mitja':
        valA = a.mitjaSaldo !== undefined && a.mitjaSaldo !== null ? a.mitjaSaldo : (a.mitja || 0);
        valB = b.mitjaSaldo !== undefined && b.mitjaSaldo !== null ? b.mitjaSaldo : (b.mitja || 0);
        return dir * (valA - valB);
      case 'desviamentMitja':
        valA = (a.variacioMitjaAnys && typeof a.variacioMitjaAnys === 'object') ? (a.variacioMitjaAnys.saldo || 0) : (a.variacioMitjaAnys || 0);
        valB = (b.variacioMitjaAnys && typeof b.variacioMitjaAnys === 'object') ? (b.variacioMitjaAnys.saldo || 0) : (b.variacioMitjaAnys || 0);
        return dir * (valA - valB);
      case 'desviamentAnyAnt':
        valA = (a.variacioAnyAnterior && typeof a.variacioAnyAnterior === 'object') ? (a.variacioAnyAnterior.saldo || 0) : (a.variacioAnyAnterior || 0);
        valB = (b.variacioAnyAnterior && typeof b.variacioAnyAnterior === 'object') ? (b.variacioAnyAnterior.saldo || 0) : (b.variacioAnyAnterior || 0);
        return dir * (valA - valB);
      case 'saldo':
        valA = getNodeSaldo(a);
        valB = getNodeSaldo(b);
        return dir * (valA - valB);
      case 'ordre':
      default:
        valA = a.ordre || 0;
        valB = b.ordre || 0;
        return dir * (valA - valB);
    }
  });
};

const TreeNodeRow = ({
  node,
  level = 1,
  parentConcept = null,
  userapp,
  selectedYear,
  selectedMes,
  maxDespesa = 1,
  totalDespesaGlobal = 1,
  expandedKeys,
  onToggle,
  showDetails = false,
  sortConfig = { key: 'ordre', direction: 'asc' }
}) => {
  const navigate = useNavigate();

  const nodeKey = node.conceptePath || `${node.id}_${level}` || node.codi;
  const hasChildren = node.fills && node.fills.length > 0;
  const isOpen = expandedKeys.has(nodeKey);

  const despesa = Math.abs(node.totalDespesa || 0);
  const ingres = node.totalIngres || 0;
  const saldo = node.saldo !== undefined ? node.saldo : (ingres - despesa);
  const colorSaldo = getColorForAmount(saldo, false);
  const percentDespesa = maxDespesa > 0 ? Math.min(100, Math.round((despesa / maxDespesa) * 100)) : 0;

  // Càlcul de moviments pendents de reclassificar (directes i en subcategories)
  const reclassStats = getReclassificationStats(node);
  const hasUnclassified = reclassStats.totalPending > 0;
  const unclassifiedTooltip = (() => {
    if (!hasUnclassified) return '';
    const { directPending, descendantPending } = reclassStats;
    if (directPending > 0 && descendantPending > 0) {
      return `${directPending} moviments directes i ${descendantPending} en subcategories pendents de reclassificar`;
    }
    if (directPending > 0) {
      return `${directPending} moviments assignats directament a ${node.nom} pendents de reclassificar als fills més concrets`;
    }
    return `${descendantPending} moviments pendents de reclassificar en subcategories (desplegueu per veure-ho)`;
  })();

  // Càlcul de Pes
  const pesPercent = (() => {
    if (node.pes !== undefined && node.pes !== null) {
      if (typeof node.pes === 'string') {
        return node.pes.endsWith('%') ? node.pes : `${node.pes}%`;
      }
      if (typeof node.pes === 'number') {
        return `${Math.round(node.pes * 10) / 10}%`;
      }
      return `${node.pes}%`;
    }
    return totalDespesaGlobal > 0
      ? `${Math.round((despesa / totalDespesaGlobal) * 100)}%`
      : '0%';
  })();

  // Mètriques clàssiques
  const mitjaValor = node.mitjaSaldo !== undefined
    ? node.mitjaSaldo
    : (node.mitja !== undefined ? node.mitja : null);

  const desvMitjaValor = (node.variacioMitjaAnys && typeof node.variacioMitjaAnys === 'object')
    ? node.variacioMitjaAnys.saldo
    : (node.variacioMitjaAnys !== undefined && node.variacioMitjaAnys !== null ? node.variacioMitjaAnys : null);

  const desvAnyAntValor = (node.variacioAnyAnterior && typeof node.variacioAnyAnterior === 'object')
    ? node.variacioAnyAnterior.saldo
    : (node.variacioAnyAnterior !== undefined && node.variacioAnyAnterior !== null ? node.variacioAnyAnterior : null);

  const navigateToNodeDashboard = () => {
    const branchParams = new URLSearchParams();
    if (node.id1) branchParams.set('id1', node.id1);
    if (node.id2) branchParams.set('id2', node.id2);
    if (node.id3) branchParams.set('id3', node.id3);
    const branchQuery = branchParams.toString() ? `?${branchParams.toString()}` : '';

    if (node.id && node.id > 0) {
      if (selectedYear && selectedMes) {
        navigate(`/${userapp}/dashboard/${selectedYear}/mes/${selectedMes}/node/${node.id}${branchQuery}`);
      } else if (selectedYear) {
        navigate(`/${userapp}/dashboard/${selectedYear}/node/${node.id}${branchQuery}`);
      } else {
        navigate(`/${userapp}/dashboard/node/${node.id}${branchQuery}`);
      }
    } else {
      if (selectedYear && selectedMes) {
        navigate(`/${userapp}/dashboard/${selectedYear}/mes/${selectedMes}/concepte/${encodeURIComponent(node.nom)}`);
      } else if (selectedYear) {
        navigate(`/${userapp}/dashboard/${selectedYear}/concepte/${encodeURIComponent(node.nom)}`);
      } else {
        navigate(`/${userapp}/dashboard/concepte/${encodeURIComponent(node.nom)}`);
      }
    }
  };

  const getNodeIcon = () => {
    return (
      <ConcepteIcon
        concept={node.nom}
        code={node.codi}
        fallback={[node.concepteClassic, parentConcept]}
        tipusNode={node.tipusNode}
        level={level}
        parentConcept={parentConcept}
        onlyIcon
        sx={{ fontSize: 18 }}
      />
    );
  };

  const indentPx = (level - 1) * 22 + 8;

  // Ordenar fills segons sortConfig
  const sortedFills = useMemo(() => {
    return sortHierarchyNodes(node.fills, sortConfig);
  }, [node.fills, sortConfig]);

  return (
    <>
      <TableRow
        hover
        onClick={navigateToNodeDashboard}
        sx={{
          cursor: 'pointer',
          backgroundColor: level === 1 ? '#f8f9fa' : level === 2 ? '#ffffff' : '#fcfcfc',
          '& > *': { borderBottom: '1px solid #edf0f2' }
        }}
      >
        {/* Columna 1: Categoria / Concepte */}
        <TableCell sx={{ pl: `${indentPx}px`, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(nodeKey);
                }}
                sx={{ p: 0.2 }}
              >
                {isOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24 }} />
            )}
            {getNodeIcon()}
            <Typography
              variant="body2"
              sx={{
                fontWeight: level === 1 ? 'bold' : level === 2 ? 600 : 400,
                color: level === 1 ? 'primary.main' : 'text.primary',
                display: 'inline'
              }}
            >
              {node.nom}
            </Typography>
            {node.concepteClassic && (
              <Tooltip title={`Mapejat a concepte clàssic: ${node.concepteClassic}`}>
                <Chip
                  label={node.concepteClassic}
                  size="small"
                  sx={{
                    fontSize: '0.60rem',
                    height: 18,
                    bgcolor: '#f5f5f5',
                    color: '#555',
                    border: '1px dashed #bdbdbd'
                  }}
                />
              </Tooltip>
            )}
            {node.tipusNode && node.tipusNode !== 'CATEGORIA' && (
              <Chip
                label={node.tipusNode}
                size="small"
                sx={{
                  fontSize: '0.62rem',
                  height: 18,
                  backgroundColor: node.tipusNode === 'VEHICLE' ? '#e1f5fe' : '#f3e5f5'
                }}
              />
            )}
          </Box>
        </TableCell>

        {/* Columna 2: Núm. Moviments (Alineat a la dreta amb avis de reclassificació) */}
        <TableCell align="right" sx={{ width: 105, py: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Chip label={node.numMoviments || 0} size="small" sx={{ fontSize: '0.68rem', height: 19 }} />
            {hasUnclassified && (
              <Tooltip title={unclassifiedTooltip}>
                <WarningAmberIcon color="warning" sx={{ fontSize: 16, cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>

        {/* Columnes de Detall condicionals */}
        {showDetails && (
          <>
            {/* Columna 3: Pes (%) */}
            <TableCell align="right" sx={{ width: 100, py: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: level === 1 ? 600 : 'normal' }}>
                {pesPercent}
              </Typography>
              {despesa > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={percentDespesa}
                  sx={{ height: 3, borderRadius: 2, mt: 0.5, bgcolor: '#f0f0f0' }}
                  color="error"
                />
              )}
            </TableCell>

            {/* Columna 4: Mitjana (mida reduïda) */}
            <TableCell align="right" sx={{ width: 110, py: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {mitjaValor !== null ? formatEuro(mitjaValor) : '-'}
              </Typography>
            </TableCell>

            {/* Columna 5: Desviament Mitjana (mida reduïda) */}
            <TableCell align="right" sx={{ width: 120, py: 1 }}>
              {desvMitjaValor !== null ? (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: getColorForAmount(desvMitjaValor) }}>
                  {formatEuro(desvMitjaValor)}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                  -
                </Typography>
              )}
            </TableCell>

            {/* Columna 6: Desviament Any Anterior (mida reduïda) */}
            <TableCell align="right" sx={{ width: 120, py: 1 }}>
              {desvAnyAntValor !== null ? (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: getColorForAmount(desvAnyAntValor) }}>
                  {formatEuro(desvAnyAntValor)}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                  -
                </Typography>
              )}
            </TableCell>
          </>
        )}

        {/* Columna 7: Saldo Net / Despesa (Últim valor amb màxim protagonisme) */}
        <TableCell align="right" sx={{ width: 140, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: level === 1 ? '0.95rem' : '0.88rem', color: colorSaldo }}>
            {formatEuro(saldo)}
          </Typography>
          {ingres > 0 && despesa > 0 && (
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary' }}>
              +{formatEuro(ingres)} / -{formatEuro(despesa)}
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Renderitzat de fills pla per garantir alineació exacta de columnes entre tots els nivells */}
      {hasChildren && isOpen && sortedFills.map((childNode) => (
        <TreeNodeRow
          key={childNode.id || childNode.codi}
          node={childNode}
          level={level + 1}
          parentConcept={node.nom}
          userapp={userapp}
          selectedYear={selectedYear}
          selectedMes={selectedMes}
          maxDespesa={despesa > 0 ? despesa : maxDespesa}
          totalDespesaGlobal={totalDespesaGlobal}
          expandedKeys={expandedKeys}
          onToggle={onToggle}
          showDetails={showDetails}
          sortConfig={sortConfig}
        />
      ))}
    </>
  );
};

const DashboardJerarquicPage = () => {
  const { userapp, anyo: paramAnyo } = useParams();
  const { ctxUser } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken;
  const navigate = useNavigate();
  const { onBack } = useBack();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(paramAnyo ? parseInt(paramAnyo, 10) : currentYear);
  const [selectedMes, setSelectedMes] = useState('');
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(new Set()); // Per defecte tot col·lapsat
  const [showDetails, setShowDetails] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'ordre', direction: 'asc' }); // Per defecte ordre BBDD

  useEffect(() => {
    if (paramAnyo) {
      setSelectedYear(parseInt(paramAnyo, 10));
    }
  }, [paramAnyo]);

  // Carregar dades del backend
  useEffect(() => {
    if (!userapp) return;
    setLoading(true);
    getResumJerarquic(jwtToken, userapp, selectedYear || undefined, selectedMes || undefined)
      .then((data) => {
        setTreeData(data || []);
        // Per defecte tot col·lapsat
        setExpandedKeys(new Set());
      })
      .catch((err) => {
        console.error('Error al carregar resum jeràrquic:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userapp, jwtToken, selectedYear, selectedMes]);

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Ordenació configurable de l'arbre
  const sortedTreeData = useMemo(() => {
    return sortHierarchyNodes(treeData, sortConfig);
  }, [treeData, sortConfig]);

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    if (!newYear) {
      setSelectedMes('');
    }
    if (newYear) {
      navigate(`/${userapp}/jerarquic/${newYear}`);
    } else {
      navigate(`/${userapp}/jerarquic`);
    }
  };

  const handleMesChange = (e) => {
    setSelectedMes(e.target.value);
  };

  // Funció per recollir totes les claus dels nodes amb fills
  const collectAllExpandableKeys = (nodes, lvl = 1) => {
    const keys = new Set();
    const walk = (list, l) => {
      list.forEach(n => {
        const k = n.conceptePath || `${n.id}_${l}` || n.codi;
        if (n.fills && n.fills.length > 0) {
          keys.add(k);
          walk(n.fills, l + 1);
        }
      });
    };
    walk(nodes, lvl);
    return keys;
  };

  const handleExpandAll = () => {
    setExpandedKeys(collectAllExpandableKeys(treeData));
  };

  const handleCollapseAll = () => {
    setExpandedKeys(new Set());
  };

  const handleToggle = (key) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Calcular totals globals
  const totalDespesaGlobal = treeData.reduce((acc, n) => acc + Math.abs(n.totalDespesa || 0), 0);
  const totalIngresGlobal = treeData.reduce((acc, n) => acc + (n.totalIngres || 0), 0);
  const totalSaldoGlobal = totalIngresGlobal - totalDespesaGlobal;
  const totalMovimentsGlobal = treeData.reduce((acc, n) => acc + (n.numMoviments || 0), 0);

  return (
    <Box sx={{ p: 2, maxWidth: 1250, margin: '0 auto' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <AccountTreeIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Resum Jeràrquic de Categories
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Botons per Expandir / Col·lapsar tot */}
          <ButtonGroup size="small" variant="outlined">
            <Button startIcon={<UnfoldMore />} onClick={handleExpandAll} sx={{ fontSize: '0.75rem' }}>
              Expandir tot
            </Button>
            <Button startIcon={<UnfoldLess />} onClick={handleCollapseAll} sx={{ fontSize: '0.75rem' }}>
              Col·lapsar tot
            </Button>
          </ButtonGroup>

          {/* Botó per Mostrar / Ocultar Detalls (Pes, Mitja, Desviacions) */}
          <Button
            size="small"
            variant={showDetails ? 'contained' : 'outlined'}
            color="secondary"
            startIcon={showDetails ? <VisibilityOff /> : <Visibility />}
            onClick={() => setShowDetails(prev => !prev)}
            sx={{ fontSize: '0.75rem' }}
          >
            {showDetails ? 'Ocultar detalls' : 'Mostrar detalls'}
          </Button>

          {/* Selector d'Any */}
          <Select
            value={selectedYear || ''}
            size="small"
            onChange={handleYearChange}
            displayEmpty
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">Històric Global</MenuItem>
            {listAnys().map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>

          {/* Selector de Mes (actiu si s'ha seleccionat un any) */}
          <Select
            value={selectedMes || ''}
            size="small"
            onChange={handleMesChange}
            displayEmpty
            disabled={!selectedYear}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">Tot l'Any</MenuItem>
            {listMesos.map((m) => (
              <MenuItem key={m.key} value={m.key}>{m.name}</MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      {/* Targetes de Totals Globals */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Card sx={{ bgcolor: '#fff5f5', borderLeft: '4px solid #f44336' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Despeses</Typography>
              <Typography variant="h6" color="error.main" fontWeight="bold">
                {formatEuro(totalDespesaGlobal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <Card sx={{ bgcolor: '#f1f8e9', borderLeft: '4px solid #4caf50' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Ingressos</Typography>
              <Typography variant="h6" color="success.main" fontWeight="bold">
                {formatEuro(totalIngresGlobal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <Card sx={{ bgcolor: '#e8f4fd', borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Saldo Agregat</Typography>
              <Typography variant="h6" color={getColorForAmount(totalSaldoGlobal)} fontWeight="bold">
                {formatEuro(totalSaldoGlobal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 3 }}>
          <Card sx={{ bgcolor: '#fafafa', borderLeft: '4px solid #9e9e9e' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Moviments Categoritzats</Typography>
              <Typography variant="h6" fontWeight="bold">
                {totalMovimentsGlobal}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Taula en Arbre amb Rollup i Columnes Clàssiques */}
      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer sx={{ maxHeight: '72vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f0f4f8', fontWeight: 'bold', fontSize: '0.82rem' } }}>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'concepte'}
                    direction={sortConfig.key === 'concepte' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('concepte')}
                  >
                    Categoria / Concepte
                  </TableSortLabel>
                </TableCell>

                <TableCell align="right" sx={{ width: 105 }}>
                  <TableSortLabel
                    active={sortConfig.key === 'numMoviments'}
                    direction={sortConfig.key === 'numMoviments' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('numMoviments')}
                  >
                    Moviments
                  </TableSortLabel>
                </TableCell>

                {showDetails && (
                  <>
                    <TableCell align="right" sx={{ width: 100 }}>
                      <TableSortLabel
                        active={sortConfig.key === 'pes'}
                        direction={sortConfig.key === 'pes' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('pes')}
                      >
                        Pes
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="right" sx={{ width: 110, fontSize: '0.78rem' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'mitja'}
                        direction={sortConfig.key === 'mitja' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('mitja')}
                      >
                        Mitja
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="right" sx={{ width: 120, fontSize: '0.78rem' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'desviamentMitja'}
                        direction={sortConfig.key === 'desviamentMitja' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('desviamentMitja')}
                      >
                        Desv. Mitjana
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="right" sx={{ width: 120, fontSize: '0.78rem' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'desviamentAnyAnt'}
                        direction={sortConfig.key === 'desviamentAnyAnt' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('desviamentAnyAnt')}
                      >
                        Any Anterior
                      </TableSortLabel>
                    </TableCell>
                  </>
                )}

                <TableCell align="right" sx={{ width: 140, fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'saldo'}
                    direction={sortConfig.key === 'saldo' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('saldo')}
                  >
                    Saldo
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTreeData.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={showDetails ? 7 : 3} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No s'han trobat categories o dades per a aquest període.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedTreeData.map((node) => (
                <TreeNodeRow
                  key={node.id || node.codi}
                  node={node}
                  level={1}
                  parentConcept={null}
                  userapp={userapp}
                  selectedYear={selectedYear}
                  selectedMes={selectedMes}
                  maxDespesa={totalDespesaGlobal}
                  totalDespesaGlobal={totalDespesaGlobal}
                  expandedKeys={expandedKeys}
                  onToggle={handleToggle}
                  showDetails={showDetails}
                  sortConfig={sortConfig}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DashboardJerarquicPage;
